import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { Field, formPropTypes, SubmissionError } from 'redux-form';

import cls from 'classnames';

import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfToday from 'date-fns/startOfToday';
import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import _noop from 'lodash/noop';
import _pick from 'lodash/pick';

import Button from '@ott/button';
import Datepicker from '@ott/datepicker';
import FirstErrorShower from '@ott/first-error-shower';
import GeoAutocompleteNext from '@ott/geo-autocomplete-next';
import { l10n } from '@ott/l10n';
import { withLayoutContext } from '@ott/layout-context';
import SearchHistory from '@ott/search-history';
import { withEnterSubmit } from '@ott/utility-enter-submit';

import {
  CITY_PARAMS,
  DATE_PARAMS,
  DATES_FORM_FIELD,
  DEFAULT_SUGGEST_CITIES,
  DEPTH_OF_SEARCH_IN_DAYS,
  LOCATION_SEARCH_RESULTS,
  SEARCH_FORM_FIELD,
  STORAGE_LAST_SEARCHES_PATH,
} from '../constants';
import CustomOptionRenderer from './blocks/CustomOptionRenderer/CustomOptionRenderer';
import getLocatorName from './helpers/getLocatorName';
import selectedLocationPropTypes from './helpers/selectedLocationPropTypes';

import store from 'store';

import styles from './ActivitiesSearchForm.scss';

const prepareDateForBack = (date) => (date ? format(date, 'yyyyMMdd') : undefined);
const parseDate = (date) => (date ? parse(date, 'yyyyMMdd', new Date()) : null);
const checkIfDatesAreActual = (startDate, endDate) => {
  return startDate && endDate && startDate >= startOfToday() && startDate <= endDate;
};

@withLayoutContext
@withEnterSubmit({ shouldFieldHasFocus: true })
class ActivitiesSearchForm extends PureComponent {
  static propTypes = {
    ...formPropTypes,
    initialSearch: PropTypes.shape({
      name: PropTypes.string,
      iata: PropTypes.string,
      start_date: PropTypes.string,
      end_date: PropTypes.string,
    }),
    onSubmit: PropTypes.func,
    change: PropTypes.func.isRequired,
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        activitiesCount: PropTypes.number,
        country: PropTypes.string,
        ...selectedLocationPropTypes,
      })
    ),
    selectedLocation: PropTypes.shape(selectedLocationPropTypes),
    isLoading: PropTypes.bool,
    formErrors: PropTypes.shape({
      city: PropTypes.string,
    }),
    // actions
    selectLocation: PropTypes.func.isRequired,
    fetchLocations: PropTypes.func.isRequired,
    setQueue: PropTypes.func.isRequired,
    /* пока используется только для того чтобы закрыть модалку инфо панели, если ищем из неё */
    onStartSearch: PropTypes.func,
    redirectCallback: PropTypes.func.isRequired,
    page: PropTypes.string,
    showOnlineActivities: PropTypes.bool,
    showDefaultCities: PropTypes.bool,
    childrenClassNames: PropTypes.shape({
      form: PropTypes.string,
    }),
  };

  static defaultProps = {
    locations: [],
    selectedLocation: {},
    formErrors: {},
    onStartSearch: _noop,
    // eslint-disable-next-line react/default-props-match-prop-types
    trackTagClick: _noop,
    isLoading: false,
    childrenClassNames: {},
    showOnlineActivities: true,
    showDefaultCities: true,
  };

  state = {
    submitTime: null,
  };

  componentDidMount() {
    const { showDefaultCities } = this.props;

    let lastSearches = store.get(STORAGE_LAST_SEARCHES_PATH) || [];
    if (showDefaultCities && !lastSearches.length) {
      lastSearches = DEFAULT_SUGGEST_CITIES;
    }

    const { initialSearch = {}, setQueue } = this.props;

    if (initialSearch.name && initialSearch.iata) {
      this.handleLocationSelect(initialSearch);
      this.setDates(initialSearch);

      store.set(STORAGE_LAST_SEARCHES_PATH, lastSearches);
      setQueue(lastSearches.filter(({ name }) => name !== initialSearch.name));
    } else if (lastSearches.length) {
      const [lastSearch, ...restSearches] = lastSearches;

      this.handleLocationSelect(lastSearch);
      this.setDates(lastSearch);

      store.set(STORAGE_LAST_SEARCHES_PATH, lastSearches);
      setQueue(restSearches);
    }
  }

  setDates = (item) => {
    const { change } = this.props;

    const startDate = parseDate(item.start_date);
    const endDate = parseDate(item.end_date);

    if (checkIfDatesAreActual(startDate, endDate)) {
      change(DATES_FORM_FIELD, {
        startDate,
        endDate,
      });
    }
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  useAsSuggestItemKeyGetter = (location = {}) => location[CITY_PARAMS.ID];

  handleSuggestChange = _debounce((term) => {
    const { change, fetchLocations } = this.props;

    change(SEARCH_FORM_FIELD, term);
    if (term.length > 0) {
      fetchLocations(term);
    }
  }, 300);

  handleLocationSelect = (nextLocation = {}) => {
    const { selectLocation, change } = this.props;
    selectLocation(nextLocation);

    change(SEARCH_FORM_FIELD, nextLocation[CITY_PARAMS.NAME]);
  };

  handleDateSelect = (startDate, endDate) => {
    const { change, isMobileLayout } = this.props;

    change(DATES_FORM_FIELD, {
      startDate,
      endDate,
    });

    if (isMobileLayout) {
      /* eslint-disable-next-line no-underscore-dangle, react/no-unused-class-component-methods */
      this._focusNextControl = true;
    }
  };

  resetDates = () => {
    const { change } = this.props;

    change(DATES_FORM_FIELD, {
      startDate: null,
      endDate: null,
    });
  };

  renderDatepicker = ({ input, meta, ...staticProps }) => {
    return (
      <Datepicker
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...staticProps}
        input={input}
        meta={meta}
        calendarPlacement="right"
        placeholder="Любые даты"
        isResettable={true}
        endDateIsOptional={true}
        foldSameDate={true}
        maxDate={addDays(startOfToday(), DEPTH_OF_SEARCH_IN_DAYS)}
        onReset={this.resetDates}
        childrenClassNames={{
          input: styles.datepickerInput,
        }}
        onDateSelect={this.handleDateSelect}
      />
    );
  };

  getCurrentSearchParams(data) {
    const { dates = {} } = data;
    const { selectedLocation } = this.props;

    const currentSearch = _pick(selectedLocation, [
      CITY_PARAMS.ID,
      CITY_PARAMS.NAME,
      CITY_PARAMS.SIGHT_ID,
    ]);

    const preparedStartDateForBack = prepareDateForBack(dates.startDate);
    currentSearch[DATE_PARAMS.START_DATE] = preparedStartDateForBack;
    currentSearch[DATE_PARAMS.END_DATE] =
      prepareDateForBack(dates.endDate) || preparedStartDateForBack;

    return currentSearch;
  }

  updateLastSearches(currentSearch) {
    const { setQueue } = this.props;

    const lastSearches = store.get(STORAGE_LAST_SEARCHES_PATH) || [];
    const filteredLastSearches = lastSearches.filter(({ name }) => name !== currentSearch.name);
    const newLastSearches = [currentSearch, ...filteredLastSearches].slice(0, 6);

    setQueue(newLastSearches.slice(1));
    store.set(STORAGE_LAST_SEARCHES_PATH, newLastSearches);
  }

  goToSearch(currentSearch) {
    const { onStartSearch, redirectCallback } = this.props;

    const params = {
      [CITY_PARAMS.ID]: currentSearch[CITY_PARAMS.ID],
      [DATE_PARAMS.START_DATE]: currentSearch[DATE_PARAMS.START_DATE],
      [DATE_PARAMS.END_DATE]: currentSearch[DATE_PARAMS.END_DATE],
    };

    // работа с фильтрами
    if (currentSearch.sightId) {
      params.bySight = currentSearch.sightId;
    }

    onStartSearch();
    redirectCallback(LOCATION_SEARCH_RESULTS, params);
  }

  submit = (data) => {
    const { selectedLocation } = this.props;

    if (_isEmpty(selectedLocation)) {
      throw new SubmissionError({
        city: l10n('required.required'),
      });
    }

    // зачем?
    this.handleLocationSelect(selectedLocation);

    const currentSearchParams = this.getCurrentSearchParams(data);
    this.updateLastSearches(currentSearchParams);
    this.goToSearch(currentSearchParams);
  };

  handleSubmit = (e) => {
    const { handleSubmit } = this.props;

    if (e) {
      e.preventDefault();
    }

    this.setState({
      submitTime: new Date(),
    });

    return handleSubmit((data) => this.submit(data))();
  };

  handleHistoryItemClick = (item) => {
    if (item.url) {
      // eslint-disable-next-line no-restricted-globals
      location.href = item.url;

      return;
    }

    this.handleLocationSelect(item.raw);
    this.setDates(item.raw);

    setTimeout(this.handleSubmit, 0);
  };

  renderSearchHistory() {
    const { queue, page, showOnlineActivities, isMobileLayout } = this.props;

    if (typeof localStorage === 'undefined') {
      return null;
    }

    const items = [];

    if (showOnlineActivities) {
      items.push({
        from: 'Онлайн-экскурсии',
        url: 'https://www.onetwotrip.com/ru/activities/online/',
      });
    }

    if (queue.length) {
      const slicedQueue = isMobileLayout ? queue.slice(0, 2) : queue;

      const itemsFromQueue = slicedQueue.map((item) => {
        const historyItem = {
          from: item.name,
          fromDate: parseDate(item.start_date),
          toDate: parseDate(item.end_date),
          raw: item,
        };

        const fromDate = parseDate(item.start_date);
        const toDate = parseDate(item.end_date);

        if (fromDate && toDate) {
          Object.assign(historyItem, {
            fromDate,
            toDate,
            isRT: true,
          });
        }

        return historyItem;
      });

      items.push(...itemsFromQueue);
    }

    if (!items.length) {
      return null;
    }

    return (
      <SearchHistory
        className={styles.searchHistory}
        handleSearch={this.handleHistoryItemClick}
        items={items}
        project="activities"
        page={page}
        foldSameDate={true}
        itemsHaveLimitedWidth={true}
      />
    );
  }

  renderGeoAutocomplete = (field) => {
    const { input, meta, triggerProps, overlayProps, ...props } = field;

    return (
      <GeoAutocompleteNext
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        triggerProps={{
          ...input,
          ...triggerProps,
          initial: meta.initial,
        }}
        overlayProps={{
          ...overlayProps,
          size: 'custom',
          optionRendererProps: {
            CustomRenderer: CustomOptionRenderer,
          },
        }}
        withArrow={false}
      />
    );
  };

  render() {
    const { formErrors, submitFailed, locations, childrenClassNames, formRef, submitRef } =
      this.props;
    const { submitTime } = this.state;

    return (
      <form
        ref={formRef}
        onSubmit={this.handleSubmit}
        className={cls(styles.ActivitiesSearchForm, childrenClassNames.form)}
        data-locator="activities-search-form"
        data-gtm-locator="activities-search-form"
      >
        <FirstErrorShower
          errors={formErrors}
          time={submitTime}
          show={submitFailed}
          container={this}
        />

        <div className={styles.fields}>
          <div className={styles.suggestBlock}>
            <Field
              name="city"
              component={this.renderGeoAutocomplete}
              triggerProps={{
                placeholder: 'Поиск по городу',
                onSearch: this.handleSuggestChange,
                className: styles.geoAutocomplete,
                title: 'Город или регион',
              }}
              overlayProps={{
                options: locations,
              }}
              props={{
                onChange: this.handleLocationSelect,
              }}
            />
          </div>

          <div className={styles.datesBlock}>
            <Field name="dates" component={this.renderDatepicker} />
          </div>

          <Button
            innerRef={submitRef}
            type="submit"
            specificLocators={getLocatorName('submit-button')}
            className={styles.buttonSubmit}
          >
            {l10n('activitiesSearchForm.searchButton')}
          </Button>
        </div>

        {this.renderSearchHistory()}
      </form>
    );
  }
}

export default ActivitiesSearchForm;
